import React from 'react';
import { Router, createBrowserHistory, useRouter } from 'react-resource-router';
import { Root, Main } from '@atlaskit/navigation-system';
import { AppProvider } from '@atlaskit/app-provider/app-provider';
import { useColorMode } from '@atlaskit/app-provider/use-color-mode';
import { useSetColorMode } from '@atlaskit/app-provider/use-set-color-mode';
import { getThemeStyles } from '@atlaskit/tokens/get-theme-styles';
import { routes } from './routes';
import { NavigationBar } from './components/NavigationBar';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { IntlProvider } from 'react-intl';
import { UserProvider } from '../auth/user-provider';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { PanelProvider, usePanelController } from './contexts/PanelContext';
import { LabsProvider } from './pages/labs/home/context/LabsContext';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { LeftSidebarProvider } from './contexts/LeftSidebarContext';
import { AsideProvider } from './contexts/AsideContext';
import { RightSidePanel, RightAsidePanel, MainWithLeftSidebar } from './components/AppNavigation';
import { useAtomValue } from 'jotai';
import { IsFocusModeAtom } from './pages/graph-explorer/home/ObjectsVisualizer/atoms/focusAtoms';
import { AnalyticsProvider } from '../common/analytics/AnalyticsProvider';
import { initAnalyticsClient } from '../common/analytics/analytics-client';
import { ExternalUserRouteGate } from './routes/ExternalUserRouteGate';
import { QueryBuilderTopNavigation } from './pages/query-builder/components/QueryBuilderNavigation';
import { useQueryBuilderColorMode } from './pages/query-builder/theme';
import { queryBuilderViewByRouteName } from './routes/query-builder';
import { LabsDarkNavigation } from './pages/labs/home/components/LabsDarkNavigation';
import { useLabsColorMode } from './pages/labs/theme';

const analyticsClient = initAnalyticsClient();

const styles = cssMap({
	rootContainer: {
		height: '100vh',
	},
});

/**
 * Conditionally renders the top NavigationBar.
 * Hidden during Graph Explorer focus mode so the full-screen overlay
 * doesn't need to fight parent stacking contexts.
 */
const ConditionalNavigationBar = (): JSX.Element | null => {
	const isFocusMode = useAtomValue(IsFocusModeAtom);
	const [{ route }] = useRouter();
	const [queryBuilderColorMode, setQueryBuilderColorMode] = useQueryBuilderColorMode();
	const [labsColorMode, setLabsColorMode] = useLabsColorMode();
	const panel = usePanelController();
	const isLabs = globalThis.location.pathname.startsWith('/labs');
	const isQueryBuilder = route.name.startsWith('query-builder-');
	const isDarkLabs = isLabs && labsColorMode === 'dark';
	if (isFocusMode) {
		return null;
	}
	if (isQueryBuilder) {
		const navigate = (path: string) => {
			globalThis.history.pushState(null, '', path);
			globalThis.dispatchEvent(new PopStateEvent('popstate'));
		};
		return (
			<QueryBuilderTopNavigation
				activeView={queryBuilderViewByRouteName[route.name] ?? 'builder'}
				colorMode={queryBuilderColorMode}
				onNavigate={navigate}
				onColorModeChange={setQueryBuilderColorMode}
			/>
		);
	}
	if (isDarkLabs) {
		return (
			<LabsDarkNavigation
				onThemeChange={(theme) => {
					if (theme === 'light') {
						panel.closeIfOwned('twg-labs-dark');
					}
					setLabsColorMode(theme === 'light' ? 'light' : 'dark');
				}}
			/>
		);
	}
	if (isLabs) {
		return (
			<NavigationBar
				colorMode={labsColorMode}
				onThemeChange={(theme) => setLabsColorMode(theme === 'light' ? 'light' : 'dark')}
			/>
		);
	}
	return <NavigationBar />;
};

const WorkbenchThemeController = (): null => {
	const [{ route }] = useRouter();
	const colorMode = useColorMode();
	const setColorMode = useSetColorMode();
	const [queryBuilderColorMode] = useQueryBuilderColorMode();
	const [labsColorMode] = useLabsColorMode();
	const isQueryBuilder = route.name.startsWith('query-builder-');
	const isLabs = globalThis.location.pathname.startsWith('/labs');
	const previousColorMode = React.useRef<ReturnType<typeof useColorMode>>('light');
	const isApplied = React.useRef(isQueryBuilder || isLabs);
	const workbenchColorMode = isQueryBuilder
		? queryBuilderColorMode
		: isLabs
			? labsColorMode
			: undefined;

	React.useLayoutEffect(() => {
		if (workbenchColorMode) {
			if (!isApplied.current) {
				previousColorMode.current = colorMode;
				isApplied.current = true;
			}
			if (colorMode !== workbenchColorMode) {
				setColorMode(workbenchColorMode);
			}
			return;
		}

		if (isApplied.current) {
			isApplied.current = false;
			if (colorMode !== previousColorMode.current) {
				setColorMode(previousColorMode.current);
			}
		}
	}, [colorMode, setColorMode, workbenchColorMode]);

	return null;
};

export const App = (): JSX.Element => {
	const [queryBuilderColorMode] = useQueryBuilderColorMode();
	const [labsColorMode] = useLabsColorMode();
	const defaultColorMode = globalThis.location.pathname.startsWith('/query-builder')
		? queryBuilderColorMode
		: globalThis.location.pathname.startsWith('/labs')
			? labsColorMode
			: 'light';

	React.useEffect(() => {
		// Load both color themes before the first toggle needs the other one.
		void getThemeStyles({ colorMode: 'auto' });
	}, []);

	return (
		<RelayEnvironmentProvider>
			<IntlProvider locale="en">
				<AnalyticsProvider analyticsClient={analyticsClient}>
					<AppProvider defaultColorMode={defaultColorMode}>
						<FlagsProvider>
							<UserProvider>
								<Router history={createBrowserHistory()} routes={routes}>
									<WorkbenchThemeController />
									<Box xcss={styles.rootContainer}>
										<ExternalUserRouteGate>
											<Root>
												<LabsProvider>
													<PanelProvider>
														<LeftSidebarProvider>
															<AsideProvider>
																{/* TopNav — hidden during Graph Explorer focus mode */}
																<ConditionalNavigationBar />
																{/* Main */}
																<Main>
																	<MainWithLeftSidebar />
																</Main>
																{/* Aside */}
																<RightAsidePanel />
																{/* Panel */}
																<RightSidePanel />
															</AsideProvider>
														</LeftSidebarProvider>
													</PanelProvider>
												</LabsProvider>
											</Root>
										</ExternalUserRouteGate>
									</Box>
								</Router>
							</UserProvider>
						</FlagsProvider>
					</AppProvider>
				</AnalyticsProvider>
			</IntlProvider>
		</RelayEnvironmentProvider>
	);
};
